// W3C Trace Context propagation for browser → backend correlation. Context is
// threaded explicitly so concurrent uploads cannot share an accidental parent.
import {
  context as otelContext,
  trace,
  TraceFlags,
  SpanStatusCode,
  type Span,
  type SpanOptions,
} from "@opentelemetry/api";
import { getTracer } from "./telemetry";

export type SpanContext = {
  traceId: string;
  spanId: string;
  traceFlags: number;
};

function asSpanContext(span: Span): SpanContext {
  const spanContext = span.spanContext();
  return {
    traceId: spanContext.traceId,
    spanId: spanContext.spanId,
    traceFlags: spanContext.traceFlags,
  };
}

function contextWithParent(parentSpan?: SpanContext) {
  if (!parentSpan) return otelContext.active();

  const parent = trace.wrapSpanContext({
    ...parentSpan,
    isRemote: false,
    traceState: undefined,
  });
  return trace.setSpan(otelContext.active(), parent);
}

export function startSpan(
  name: string,
  options: SpanOptions,
  parentSpan?: SpanContext,
): { span: Span; context: SpanContext } {
  const span = getTracer().startSpan(
    name,
    options,
    contextWithParent(parentSpan),
  );
  return { span, context: asSpanContext(span) };
}

export async function withSpan<T>(
  name: string,
  options: SpanOptions,
  fn: (spanContext: SpanContext) => Promise<T>,
  parentSpan?: SpanContext,
): Promise<T> {
  const { span, context } = startSpan(name, options, parentSpan);
  try {
    return await fn(context);
  } catch (error) {
    if (error instanceof Error) {
      span.recordException(error);
    }
    span.setStatus({ code: SpanStatusCode.ERROR });
    throw error;
  } finally {
    span.end();
  }
}

export function traceparent(sc: SpanContext): string {
  const sampled = sc.traceFlags & TraceFlags.SAMPLED ? "01" : "00";
  return `00-${sc.traceId}-${sc.spanId}-${sampled}`;
}

// Augment RequestInit so an explicit `parentSpan` can be threaded into the
// orval-generated API clients without losing type safety. Orval forwards the
// raw RequestInit through to customFetch, so any extra property survives the
// trip.
declare global {
  interface RequestInit {
    parentSpan?: SpanContext;
  }
}
